import React, { useState } from 'react';
import { Row, Col, Button, App, Affix, Grid } from 'antd';
import { PhoneOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import TagManager from 'react-gtm-module';
import { Title } from '../../UI/Title';
import { Disclaimer } from '../../UI/Disclaimer';
import { Button as ActionButton } from '../../UI/Button';
import { useAuth } from '../../../providers/AuthProvider';
import axios from 'axios';
import { useParams } from 'react-router';
import { formatPrice, gaEvent, getTitle } from '../../../utils/utils';
import { IPlan } from '../../../types/plans';
import {
  dotStyle,
  optionStyle,
  fixedHeaderStyle,
  titleStyle,
  priceStyle,
  cartButtonStyle,
  buttonLabelStyle,
  mobileFixedButtonStyle,
} from './styles';
import { SummaryData } from '../../../types/summary';

const { useBreakpoint } = Grid;

interface OrderSitePlanData {
  user: {
    email: string | undefined;
    name: string | undefined;
  };
  plan: {
    id: string;
    mapId: string | undefined;
    image: string;
    title: string;
    planOwnerId: string;
  };
}

export const PlanInfo = ({
  plan,
  title,
  areaTitle,
  summary,
  pricesRef,
  calendlyRef,
  ctaLabel,
  onNext,
}: {
  plan: IPlan;
  title: string;
  areaTitle: string;
  summary: SummaryData;
  pricesRef?: React.RefObject<HTMLDivElement>;
  calendlyRef?: React.RefObject<HTMLDivElement>;
  ctaLabel?: string;
  onNext?: () => void;
}) => {
  const { mapId } = useParams();
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  const { hasARModel, isModular, isManufactured, preApproved, fixPrice, preApprovedArea } = plan;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [isScrolled, setIsScrolled] = useState(false);
  const orderSitePlan = async () => {
    try {
      gaEvent('planView', 'click:order_site_plan', 'Order site plan');

      if (!user) {
        message.error('Please log in.');
        return;
      }

      const confirmOrder = () => {
        return new Promise<void>(async (resolve, reject) => {
          const hide = message.loading('Action in progress..', 10);

          try {
            const data: OrderSitePlanData = {
              user: {
                email: user.email,
                name: user.name,
              },
              plan: {
                id: plan.id!,
                mapId,
                image: plan.image,
                title: `${title} ${areaTitle}`,
                planOwnerId: plan.planOwnerId,
              },
            };

            const response = await axios.post('/api/order-site-plan', data);

            if (response.status === 200) {
              hide();
              message.success(
                `Thank you, ${user.name}! We will follow up with more details shortly.`,
                10
              );
              resolve();
            } else {
              throw new Error('Failed to process order');
            }
          } catch (error) {
            hide();
            message.error('Failed to process your order. Please try again later.');
            console.error('Order site plan error:', error);
            reject(error);
          }
        });
      };

      modal.confirm({
        title: 'Order Site Plan',
        content: 'You are sending the request to order site plan.',
        onOk: confirmOrder,
        okText: 'Yes',
        cancelText: 'No',
      });
    } catch (error) {
      console.error('Order site plan error:', error);
      message.error('An unexpected error occurred. Please try again later.');
    }
  };

  const isNextFlow = typeof onNext === 'function';
  const primaryCta = isNextFlow ? onNext : orderSitePlan;
  const primaryCtaLabel = ctaLabel ?? (isNextFlow ? 'Next' : 'Order "as is" Site Plan');
  const handleBookCall = () => {
    const tagManagerArgs = {
      dataLayer: {
        event: 'click',
        button: 'bookcall',
        page: location.pathname,
        home: getTitle(plan.meta),
        planId: plan.id,
      },
    };
    TagManager.dataLayer(tagManagerArgs);
    calendlyRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetFinancing = () => {
    const tagManagerArgs = {
      dataLayer: {
        event: 'click_get_financing',
        page: location.pathname,
        home: getTitle(plan.meta),
        planId: plan.id,
      },
    };
    TagManager.dataLayer(tagManagerArgs);
    window.open('https://www.aduloans.net/adu-estimator', '_blank');
  };

  const renderMobileFixedHeader = () => {
    return (
      <div className={fixedHeaderStyle}>
        <Row align="middle" justify="space-between">
          <Col span={16}>
            <p className={priceStyle}>
              {(isModular || isManufactured) && fixPrice
                ? `Starting from $${formatPrice(fixPrice)}`
                : null}
              {!isModular &&
                !isManufactured &&
                !fixPrice &&
                summary &&
                summary.price &&
                summary.price.min &&
                summary.price.max && (
                  <>
                    ${formatPrice(summary.price.min)} - ${formatPrice(summary.price.max)}
                    <Button
                      type="text"
                      shape="circle"
                      icon={<QuestionCircleOutlined />}
                      onClick={e => {
                        e.preventDefault();
                        pricesRef?.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                    />
                  </>
                )}
            </p>
            <p className={titleStyle}>
              {title || 'House'} {areaTitle}{' '}
            </p>
          </Col>
          <Col span={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {/* <Button
              className={cartButtonStyle}
              onClick={orderSitePlan}
              icon={<ShoppingOutlined />}
              style={{ width: '100%' }}
            /> */}
            <Button
              // className={phoneButtonStyle}
              className={cartButtonStyle}
              onClick={handleBookCall}
              icon={<PhoneOutlined />}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      {isMobile && (
        <Affix offsetTop={0} onChange={value => setIsScrolled(!!value)}>
          {isScrolled && renderMobileFixedHeader()}
        </Affix>
      )}
      {isMobile && isScrolled && (
        <div className={`${mobileFixedButtonStyle} ${!user ? 'no-user' : ''}`}>
          <div style={buttonLabelStyle}>Ready to get started?</div>
          <ActionButton
            style={{
              backgroundColor: '#EE8865',
              color: '#ffffff',
              margin: '0',
              fontWeight: 400,
            }}
            onClick={primaryCta}>
            {primaryCtaLabel}
          </ActionButton>
        </div>
      )}

      <div style={{ padding: '2em', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1 }}>
          <Row style={{ display: 'flex', flex: 1 }}>
            <Col>
              <Title>
                {title || 'House'} {areaTitle}
              </Title>
              {(isModular || isManufactured) && fixPrice ? (
                <Title>Starting from ${formatPrice(fixPrice)}</Title>
              ) : null}
              {!isModular &&
                !isManufactured &&
                !fixPrice &&
                summary &&
                summary.price &&
                summary.price.min &&
                summary.price.max && (
                  <Title>
                    Starting from ${formatPrice(summary.price.min)}
                    <Button
                      type="text"
                      shape="circle"
                      icon={<QuestionCircleOutlined />}
                      onClick={e => {
                        e.preventDefault();
                        pricesRef?.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                    />
                  </Title>
                )}
              <Disclaimer>
                Disclaimer: All calculations are estimates and provided for informational purposes
                only. Actual amounts will vary depending on location, site slope, soil, local labor
                and utility connection costs.
              </Disclaimer>
              {preApproved && (
                <span style={{ ...optionStyle, marginTop: 10, marginBottom: 0 }}>
                  <span style={dotStyle} />
                  Pre-Approved
                </span>
              )}
              {preApproved && preApprovedArea && (
                <Disclaimer style={{ margin: '0px 5px 5px', padding: 0, lineHeight: '18px' }}>
                  {[
                    preApprovedArea.states?.length > 0 ? preApprovedArea.states.join(', ') : null,
                    preApprovedArea.counties?.length > 0
                      ? preApprovedArea.counties.join(', ')
                      : null,
                    preApprovedArea.cities?.length > 0 ? preApprovedArea.cities.join(', ') : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Disclaimer>
              )}
              {isModular && (
                <span style={optionStyle}>
                  <span style={dotStyle} />
                  Modular
                </span>
              )}
              {isManufactured && (
                <span style={optionStyle}>
                  <span style={dotStyle} />
                  Manufactured
                </span>
              )}
            </Col>
          </Row>
        </div>
        <Row style={{ display: 'flex', marginTop: 'auto', paddingTop: '1em' }}>
          <Col flex="1">
            {hasARModel && (
              <ActionButton
                size="large"
                onClick={() =>
                  window.open('https://apps.apple.com/us/app/adu-viewer/id1632652791', '_blank')
                }>
                View in augmented reality
              </ActionButton>
            )}
            <ActionButton style={{ marginTop: '0px' }} onClick={handleGetFinancing}>
              Get financing
            </ActionButton>
            <div style={buttonLabelStyle}>Need guidance?</div>
            <ActionButton style={{ marginTop: '0px' }} onClick={handleBookCall}>
              Book a Call with ADU Expert
            </ActionButton>
            <div style={buttonLabelStyle}>Ready to get started?</div>
            <ActionButton
              style={{
                backgroundColor: '#EE8865',
                color: '#ffffff',
                marginTop: '0px',
                fontWeight: 400,
              }}
              onClick={primaryCta}>
              {primaryCtaLabel}
            </ActionButton>
          </Col>
        </Row>
      </div>
    </>
  );
};
